import { useRefAutoUpdate } from "@kalyzee/kast-web-sdk/web-components";
import { MeetingRoomAudienceType } from "@kalyzee/kast-web-sdk/websocket";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { usePageContext } from "../../../../../../components/navigation/PageContext";
import { PedagogyGroupContainer } from "../../../../../../components/pedagogy/containers/groupContainer";
import { LoadingView } from "../../../../../../components/utils/LoadingView";
import { openMeetingActivityRoom } from "../../../../../../helpers/meeting";
import { useMeetingContainerAPI, useMeetingSelector } from "../../../../../../hooks/meeting";
import { getCurrentExpertGroupView, useActivityData, useExpertAvailableViews, usePopulatedMeetingData } from "../../../../../../hooks/populatedData";
import { SessionMode } from "../../../../../../store/session/slices";
import { usePedagogyContext } from "../../../context";
import styles from "./group.module.css";
import { getExpertAudioValidator } from "../../../../../../helpers/audio";
import { ContainerUtils } from "../../../../../../helpers/container";

export enum MeetingPedagogyInterventionGroupPageQueryAction {
  TALK_TO_LEADER = "talk_to_leader",
  TALK_TO_ALL_LEADERS = "talk_to_all_leaders",
  TALK_TO_EVERYBODY = "talk_to_everybody",
  JOIN = "join",
}

export const MeetingPedagogyInterventionGroupPage = () => {
  const pageContext = usePageContext<SessionMode.MASTER>();
  const pageContextRef = useRefAutoUpdate(pageContext);
  const meetingContainerAPI = useMeetingContainerAPI(pageContextRef.current?.params?.id);
  const pedagogyCtx = usePedagogyContext();
  const pedagogyCtxRef = useRefAutoUpdate(pedagogyCtx);

  const [meeting] = useMeetingSelector(meetingContainerAPI, (state) => state.meeting);
  const [session] = useMeetingSelector(meetingContainerAPI, (state) => state.session);

  const populatedMeetingData = usePopulatedMeetingData(meetingContainerAPI);
  const activityData = useActivityData(populatedMeetingData);
  const availableExpertViews = useExpertAvailableViews(populatedMeetingData, activityData, pedagogyCtx?.spiedGroupId);
  const currentExpertView = availableExpertViews[0];
  const [searchParams] = useSearchParams();
  const [ready, setReady] = useState(false);

  // ---------------------------------------------------------------------- //
  // ---------------------------   UTILS ----------------------------- //
  // ---------------------------------------------------------------------- //

  const onStart = async () => {
    try {
      if (searchParams) {
        const action = searchParams.get("action");
        const groupId = searchParams.get("groupId");
        if (action === MeetingPedagogyInterventionGroupPageQueryAction.TALK_TO_LEADER) {
          if (groupId && meeting) {
            await openMeetingActivityRoom({
              id: meeting.id,
              audience: {
                type: MeetingRoomAudienceType.RoomLeader,
                values: [groupId],
              },
            });
          }
        } else if (action === MeetingPedagogyInterventionGroupPageQueryAction.TALK_TO_ALL_LEADERS) {
          if (meeting) {
            await openMeetingActivityRoom({
              id: meeting.id,
              audience: {
                type: MeetingRoomAudienceType.RoomLeader,
                values: "*",
              },
            });
          }
        } else if (action === MeetingPedagogyInterventionGroupPageQueryAction.TALK_TO_EVERYBODY) {
          if (session) {
            if (!meetingContainerAPI) return;
            await ContainerUtils.setMeetingSessionHeardByEveryoneState(meetingContainerAPI, session.id, true);
          }
        } else if (action === MeetingPedagogyInterventionGroupPageQueryAction.JOIN || !action) {
          if (groupId && meeting) {
            await openMeetingActivityRoom({
              id: meeting.id,
              audience: {
                type: MeetingRoomAudienceType.RoomMember,
                values: [groupId],
              },
            });
          }
        }
      }
    } finally {
      setReady(true);
    }
  };

  // ---------------------------------------------------------------------- //
  // ---------------------------   EFFECT ----------------------------- //
  // ---------------------------------------------------------------------- //

  useEffect(() => {
    onStart();
    pedagogyCtxRef.current?.setDisplayRemoteController(true); // display by default
  }, []);

  const view = useMemo(() => {
    return getCurrentExpertGroupView(populatedMeetingData, activityData, pedagogyCtx?.spiedGroupId)
  }, [populatedMeetingData, activityData, pedagogyCtx?.spiedGroupId]);

  useEffect(() => {
    const audioValidator = getExpertAudioValidator(view);
    const validator = pedagogyCtxRef.current?.addAudioValidator(audioValidator);
    return () => {
      validator?.flush();
    };
  }, [view]);

  // ---------------------------------------------------------------------- //
  // ---------------------------   RENDER ----------------------------- //
  // ---------------------------------------------------------------------- //

  if (!meeting || !ready) return <LoadingView dark />;

  return (
    <div className={styles.container}>
      <PedagogyGroupContainer
        meetingContainerAPI={meetingContainerAPI}
        populatedMeetingData={populatedMeetingData}
        meeting={meeting}
        context={pedagogyCtx}
        currentExpertView={currentExpertView}
        className={styles.content}
      />
    </div>
  );
};
